<template>
  <div class="loginpage">
    <div v-if="loadingSharedUser" style="margin: 5em auto; text-align: center">
      <img alt="Data loading" src="@/assets/img/snake_logo_animation_200.gif" />
      <p>Loading</p>
    </div>
    <Login
      v-else
      :redir_on_success="redirOnSuccess"
      :error="errCode"
      :initial_messages="messages"
      :initial_email="email"
      :shared_user="sharedUser"
    ></Login>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Login from "@/components/Login.vue";
import { WSClient, ApiMessage } from "@/ems/WSClient";

interface Message {
  level: string;
  text: string;
}

export interface SharedUser {
  user_id: string;
  email_hash: string;
  Email: string;
  FirstName: string;
  has_password: boolean;
}

const props = defineProps<{
  error?: string;
  user_id?: string;
  email_hash?: string;
}>();

const emit = defineEmits<{
  (e: "hide_login_bt", value: boolean): void;
}>();

const route = useRoute();
const router = useRouter();
const redirOnSuccess = ref("");
const messages = ref<Message[]>([]);
const email = ref("");
const loadingSharedUser = ref(false);
const sharedUser = ref<SharedUser | null>(null);
const apiMessages = ref<ApiMessage[]>([]);
const client = new WSClient(loadingSharedUser, apiMessages);

const errCode = computed(() => {
  return parseInt(props.error || route.query.error?.toString() || "0");
});

const fetchSharedUser = async () => {
  const userId = props.user_id || route.params.user_id?.toString();
  const emailHash = props.email_hash || route.params.email_hash?.toString();

  if (userId && emailHash) {
    loadingSharedUser.value = true;
    try {
      const resp = await client.queryWs<{ user: { Email: string; FirstName: string; has_password: boolean } }>(
        "GET",
        "organization_user_userStatus",
        {
          user_id: userId,
          hash: emailHash,
        }
      );
      if (resp?.user) {
        sharedUser.value = {
          user_id: userId,
          email_hash: emailHash,
          Email: resp.user.Email,
          FirstName: resp.user.FirstName,
          has_password: resp.user.has_password,
        };
        email.value = resp.user.Email;
      }
    } catch (e) {
      console.error("Error fetching shared user", e);
      messages.value = apiMessages.value as Message[];
    } finally {
      loadingSharedUser.value = false;
    }
  }
};

const stripBasePath = (path: string): string => {
  let basePath = (router.options.history as any).base || "/v/";
  // Ensure basePath ends with /
  if (!basePath.endsWith("/")) {
    basePath += "/";
  }
  // Strip the base path but keep the leading /
  if (path.startsWith(basePath)) {
    return "/" + path.substring(basePath.length);
  }
  // Also check without trailing slash
  const baseNoSlash = basePath.slice(0, -1);
  if (path.startsWith(baseNoSlash + "/")) {
    return "/" + path.substring(baseNoSlash.length + 1);
  }
  return path;
};

const processRedirParam = () => {
  const searchParams = new URLSearchParams(document.location.search);
  const redir = searchParams.get("redir")
    || searchParams.get("redir_on_success")
    || route.query.redir?.toString()
    || route.query.redir_on_success?.toString()
    || "";

  if (!redir || redir === "/" || redir === redirOnSuccess.value) {
    return;
  }

  redirOnSuccess.value = redir;

  if (redir === "reset") {
    sessionStorage.removeItem("afterlogin");
    return;
  }

  try {
    let pathToResolve = stripBasePath(redir);
    if (!pathToResolve.startsWith("/")) {
      pathToResolve = "/" + pathToResolve;
    }
    const resolved = router.resolve(pathToResolve);
    if (resolved.name && resolved.name !== "NotFound") {
      const destination = {
        name: resolved.name,
        params: resolved.params,
        query: resolved.query,
      };
      sessionStorage.setItem("afterlogin", JSON.stringify(destination));
    }
  } catch (e) {
    console.error("Error saving redir to afterlogin", e);
  }
};

watch(
  () => route.query,
  () => processRedirParam(),
  { immediate: true, deep: true }
);

onMounted(async () => {
  emit("hide_login_bt", true);
  processRedirParam();
  await fetchSharedUser();

  // If shared user has no password, redirect directly to signup
  if (sharedUser.value && !sharedUser.value.has_password) {
    const signupRoute: { name: string; params: { user_id: string; email_hash: string }; query?: { redir: string } } = {
      name: "signup_shared",
      params: {
        user_id: sharedUser.value.user_id,
        email_hash: sharedUser.value.email_hash,
      },
    };
    if (redirOnSuccess.value) {
      signupRoute.query = { redir: redirOnSuccess.value };
    }
    router.replace(signupRoute);
    return;
  }

  if (route.query.msg === "pass_changed") {
    messages.value = [
      {
        level: "success",
        text: "Your password has been changed. Please use it to log-in.",
      },
    ];
  }

  if (route.query.email) {
    email.value = route.query.email.toString();
  }
});
</script>

<style scoped>
.loginpage {
  position: relative;
  margin: 4.5rem 1rem 1rem 1rem;
  text-align: center;
}
</style>
